import {
    Box, Table, TableBody, TableCell, TableContainer,
    TableHead, TablePagination, TableRow, Typography,
    InputAdornment, TextField, Chip, IconButton, TableSortLabel,
} from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SearchIcon from '@mui/icons-material/Search'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import PeopleAltIcon from '@mui/icons-material/PeopleAlt'
import SyncIcon from '@mui/icons-material/Sync'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { ApiGetUserAssets } from '../../apiService/Core/Apicall'
import { getMeta, fmt, CurrencyBadge, PlatformAsset } from './PlatformBalance'

interface UserAsset {
    userId: string
    firstName: string
    lastName: string
    email: string
    currency: string
    assetType: string
    balance: number
    available: number
    held: number
    avgBuyPrice: number
    realizedPnl: number
    assetName: string
}

type SortKey = 'firstName' | 'balance' | 'available' | 'held' | 'avgBuyPrice' | 'realizedPnl'
type SortDir = 'asc' | 'desc'

interface HeadCol {
    id: string
    label: string
    sortKey?: SortKey
}

const HEAD_COLS: HeadCol[] = [
    { id: '#', label: '#' },
    { id: 'user', label: 'User', sortKey: 'firstName' },
    { id: 'assetType', label: 'Asset Type' },
    { id: 'balance', label: 'Balance', sortKey: 'balance' },
    { id: 'available', label: 'Available', sortKey: 'available' },
    { id: 'held', label: 'Held', sortKey: 'held' },
    { id: 'avgBuy', label: 'Avg Buy Price', sortKey: 'avgBuyPrice' },
    { id: 'pnl', label: 'Realized PnL', sortKey: 'realizedPnl' },
]

const AssetDetailPage: React.FC = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { currency } = useParams<{ currency: string }>()

    const asset: PlatformAsset | null = location.state?.asset ?? null

    const [userAssets, setUserAssets] = useState<UserAsset[]>([])
    const [userLoading, setUserLoading] = useState(false)
    const [userSearch, setUserSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [userPage, setUserPage] = useState(0)
    const [userRowsPerPage, setUserRowsPerPage] = useState(10)
    const [userTotal, setUserTotal] = useState(0)
    const [sortKey, setSortKey] = useState<SortKey>('balance')
    const [sortDir, setSortDir] = useState<SortDir>('desc')

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(userSearch), 400)
        return () => clearTimeout(t)
    }, [userSearch])

    const fetchUserAssets = useCallback(async () => {
        if (!currency) return
        setUserLoading(true)
        try {
            const params = new URLSearchParams()
            params.append('asset', currency)
            params.append('page', String(userPage + 1))
            params.append('limit', String(userRowsPerPage))
            if (debouncedSearch) params.append('search', debouncedSearch)
            const res = await ApiGetUserAssets(`?${params.toString()}`)
            if (res?.status) {
                setUserAssets(res.data.data)
                setUserTotal(res.data.total)
            }
        } catch (err) {
            console.error('Error fetching user assets:', err)
        } finally {
            setUserLoading(false)
        }
    }, [currency, userPage, userRowsPerPage, debouncedSearch])

    useEffect(() => { fetchUserAssets() }, [fetchUserAssets])

    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc')
        } else {
            setSortKey(key)
            setSortDir('asc')
        }
        setUserPage(0)
    }

    const sortedAssets = useMemo(() => {
        return [...userAssets].sort((a, b) => {
            let av: any = a[sortKey]
            let bv: any = b[sortKey]
            if (typeof av === 'string') av = av.toLowerCase()
            if (typeof bv === 'string') bv = bv.toLowerCase()
            if (av < bv) return sortDir === 'asc' ? -1 : 1
            if (av > bv) return sortDir === 'asc' ? 1 : -1
            return 0
        })
    }, [userAssets, sortKey, sortDir])

    const m = getMeta(currency ?? '')

    return (
        <div className='common-layout flex-column' style={{ minHeight: '100vh' }}>
            <Box className="border-10 p-2 px-4 mixed-orange mb-4 both-circle d-flex align-items-center justify-content-between"
                sx={{ position: 'relative', overflow: 'hidden' }}
            >
                <Box className="d-flex gap-3 align-items-center">
                    <IconButton sx={{ ':hover': { backgroundColor: '#ffffff1A' } }} onClick={() => navigate('/admin/platform-balance')}>
                        <ArrowBackIcon sx={{ color: '#ffffff' }} />
                    </IconButton>
                    <Box sx={{
                        width: 52, height: 52, borderRadius: '14px',
                        background: m.bg, border: `1px solid ${m.border}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                        <Typography fontSize={14} fontWeight={900} color={m.color} sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {(currency ?? '').slice(0, 3).toUpperCase()}
                        </Typography>
                    </Box>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.3 }}>
                            <Typography className='fs-22 text-white fw-medium'>{m.label}</Typography>
                            <CurrencyBadge currency={currency ?? ''} />
                        </Box>
                        {asset && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography fontSize={11} color="rgba(255,255,255,0.5)">
                                    Provider: {asset.provider} &nbsp;·&nbsp; {asset.currencyType}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                    <Typography fontSize={11} color="rgba(255,255,255,0.5)" textTransform="uppercase" letterSpacing="0.08em">Total Holders</Typography>
                    <Typography className='fs-20 text-white fw-medium'>{userTotal}</Typography>
                </Box>
            </Box>


            <Box className="bg-dark-color border-10 p-0" sx={{ overflow: 'hidden' }}>

                <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    px: 3, py: 2,
                    borderBottom: '1px solid #1e1e1e',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <PeopleAltIcon sx={{ color: '#ff6a3d', fontSize: 16 }} />
                        <Typography fontSize={13} fontWeight={700} color="#e0e0e0">User Holdings</Typography>
                        <Chip
                            label={userTotal}
                            size="small"
                            sx={{
                                height: 18, fontSize: 10, fontWeight: 700,
                                background: m.bg, color: m.color,
                                border: `1px solid ${m.border}`,
                                '& .MuiChip-label': { px: 1 },
                            }}
                        />
                        {userLoading && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <SyncIcon sx={{ fontSize: 12, color: '#10b981', animation: 'spin 1.2s linear infinite', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }} />
                                <Typography fontSize={10} color="#10b981">Loading...</Typography>
                            </Box>
                        )}
                    </Box>
                    <TextField
                        placeholder="Search by user..."
                        value={userSearch}
                        onChange={e => { setUserSearch(e.target.value); setUserPage(0) }}
                        size="small"
                        sx={searchStyle}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon className='color-primary' sx={{ fontSize: 16 }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                <TableContainer sx={{ maxHeight: 'calc(100vh - 520px)', minHeight: 300 }}>
                    <Table stickyHeader sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid #1e1e1e' } }}>
                        <TableHead>
                            <TableRow>
                                {HEAD_COLS.map(col => (
                                    <TableCell
                                        key={col.id}
                                        sortDirection={col.sortKey && sortKey === col.sortKey ? sortDir : false}
                                        sx={thStyle}
                                    >
                                        {col.sortKey ? (
                                            <TableSortLabel
                                                active={sortKey === col.sortKey}
                                                direction={sortKey === col.sortKey ? sortDir : 'asc'}
                                                onClick={() => handleSort(col.sortKey!)}
                                                sx={{
                                                    color: '#ff6a3d !important',
                                                    '& .MuiTableSortLabel-icon': {
                                                        color: '#ff6a3d !important',
                                                        opacity: sortKey === col.sortKey ? 1 : 0.3,
                                                    },
                                                    '&:hover': { color: '#ff8c66 !important' },
                                                    '&.Mui-active': { color: '#ff6a3d !important' },
                                                }}
                                            >
                                                {col.label}
                                            </TableSortLabel>
                                        ) : (
                                            col.label
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedAssets.length > 0 ? sortedAssets.map((u, i) => (
                                <TableRow
                                    key={u.userId}
                                    sx={{
                                        transition: 'background 0.15s',
                                        '&:hover': { background: '#ffffff04' },
                                        '& .MuiTableCell-root': { borderBottom: '1px solid #1e1e1e' },
                                    }}
                                >
                                    {/* # */}
                                    <TableCell sx={tdStyle}>
                                        <Typography fontSize={11} color="#3a3a3a" fontWeight={700}>
                                            {userPage * userRowsPerPage + i + 1}
                                        </Typography>
                                    </TableCell>

                                    <TableCell sx={tdStyle}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                            <Typography fontSize={12} color="#e0e0e0" fontWeight={600}>
                                                {u.firstName} {u.lastName}
                                            </Typography>
                                            <Typography fontSize={10} color="#6a6a6a" sx={{ fontFamily: 'monospace' }}>
                                                {u.email}
                                            </Typography>
                                        </Box>
                                    </TableCell>


                                    <TableCell sx={tdStyle}>
                                        <Box sx={{
                                            display: 'inline-block', px: 1.5, py: 0.4, borderRadius: '5px',
                                            fontSize: '10px', fontWeight: 500, letterSpacing: '0.04em',
                                            color: '#9a9a9a', backgroundColor: '#2a2a2a', border: '1px solid #3f3f3f',
                                            textTransform: 'uppercase',
                                        }}>
                                            {u.assetType || 'crypto'}
                                        </Box>
                                    </TableCell>

                                    <TableCell sx={tdStyle}>
                                        <Typography fontSize={13} fontWeight={700} color={m.color}>
                                            {fmt(u.balance, 8)}
                                        </Typography>
                                    </TableCell>

                                    <TableCell sx={tdStyle}>
                                        <Typography fontSize={13} color={u.available < 0 ? '#ef4444' : '#10b981'}>
                                            {fmt(u.available, 8)}
                                        </Typography>
                                    </TableCell>

                                    <TableCell sx={tdStyle}>
                                        <Typography fontSize={13} color={u.held > 0 ? '#f59e0b' : '#4a4a4a'}>
                                            {fmt(u.held, 8)}
                                        </Typography>
                                    </TableCell>

                                    <TableCell sx={tdStyle}>
                                        <Typography fontSize={12} color="#9a9a9a">
                                            ${u.avgBuyPrice?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </Typography>
                                    </TableCell>

                                    <TableCell sx={tdStyle}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            {u.realizedPnl >= 0
                                                ? <TrendingUpIcon sx={{ fontSize: 13, color: '#10b981' }} />
                                                : <TrendingDownIcon sx={{ fontSize: 13, color: '#ef4444' }} />
                                            }
                                            <Typography
                                                fontSize={12} fontWeight={600}
                                                color={u.realizedPnl >= 0 ? '#10b981' : '#ef4444'}
                                            >
                                                {u.realizedPnl >= 0 ? '+' : ''}${fmt(u.realizedPnl, 2)}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={HEAD_COLS.length} align="center" sx={{ py: 8, background: '#1a1a1a', border: 'none' }}>
                                        <AccountBalanceWalletIcon sx={{ color: '#2a2a2a', fontSize: 40, mb: 1 }} />
                                        <Typography color="#4a4a4a" fontSize={13}>
                                            {userSearch ? 'No users match your search' : 'No users hold this asset'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={userTotal}
                    page={userPage}
                    onPageChange={(_, p) => setUserPage(p)}
                    rowsPerPage={userRowsPerPage}
                    onRowsPerPageChange={e => { setUserRowsPerPage(parseInt(e.target.value, 10)); setUserPage(0) }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    showFirstButton showLastButton
                    sx={paginationStyle}
                />
            </Box>
        </div>
    )
}

export default AssetDetailPage


const tdStyle = {
    color: '#c0c0c0',
    py: 1.4,
    fontSize: 13,
    whiteSpace: 'nowrap' as const,
}

const thStyle = {
    backgroundColor: '#241815',
    color: '#ff6a3d',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    whiteSpace: 'nowrap' as const,
    py: 1.5,
    borderBottom: '1px solid #2a2a2a !important',
    position: 'sticky' as const,
    top: 0,
    zIndex: 2,
}

const paginationStyle = {
    color: '#6a6a6a',
    borderTop: '1px solid #252525',
    '& .MuiTablePagination-toolbar': { justifyContent: 'center' },
    '& .MuiTablePagination-selectLabel': { color: '#6a6a6a', margin: 0 },
    '& .MuiTablePagination-displayedRows': { color: '#6a6a6a', margin: 0 },
    '& .MuiSelect-select': { color: '#6a6a6a' },
    '& .MuiSelect-icon': { color: '#6a6a6a' },
    '& .MuiIconButton-root': { color: '#6a6a6a' },
    '& .MuiIconButton-root.Mui-disabled': { color: '#2a2a2a' },
}

const searchStyle = {
    width: 280,
    background: 'transparent',
    borderRadius: '7px',
    border: '1px solid #898989',
    transition: 'border-color 0.3s',
    '&:hover': { border: '1px solid #ff6a3d' },
    '&:focus-within': { border: '1px solid #ff6a3d' },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& input': { color: '#fff', fontSize: 12 },
    '& input::placeholder': { color: '#555', opacity: 1 },
}